// src/components/pages/admin-dash/AdminScratchStateReport.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import AdminTable from "./AdminTable";

/**
 * Admin report — scratch-off catalog size, pricing, engagement, and
 * ingestion health per state (GET /admin/reports/scratch-by-state).
 * Roadmap item 3.9.
 */
const fmtUsd = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n)
    : "—";

const fmtRelative = (iso) => {
  if (!iso) return "Never";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

export default function AdminScratchStateReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axiosInstance.get("/admin/reports/scratch-by-state");
        setRows(res.data || []);
      } catch {
        setError("Failed to load scratch-off state report.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const columns = [
    { key: "stateName", label: "State", sort: true,
      render: (r) => (
        <div>
          <div className="font-medium">{r.stateName}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>{r.stateCode}</div>
        </div>
      ) },
    { key: "activeGameCount", label: "Active games", sort: true, align: "right",
      render: (r) => <span className="tabular-nums font-medium" style={{ color: "var(--gold)" }}>{r.activeGameCount}</span> },
    { key: "averageTicketPrice", label: "Avg. ticket price", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{fmtUsd(r.averageTicketPrice)}</span> },
    { key: "usersWithPreferenceCount", label: "Users following", sort: true, align: "right",
      render: (r) => <span className="tabular-nums">{r.usersWithPreferenceCount}</span> },
    { key: "lastRunAt", label: "Last ingestion", sort: true,
      render: (r) => <span>{fmtRelative(r.lastRunAt)}</span> },
    { key: "health", label: "Health", sort: false,
      render: (r) => {
        const ok = !r.lastError;
        return (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
            style={ok
              ? { background: "var(--green-dim, rgba(34,197,94,.12))", color: "var(--green, #22c55e)" }
              : { background: "var(--red-dim)", color: "var(--red)" }}
            title={r.lastError || undefined}
          >
            {ok ? "OK" : "Failing"}
          </span>
        );
      } },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Scratch-off by state
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Catalog size, pricing, and user engagement per state, alongside the ingestion
          health already tracked for each source. Snapshot as of now.
        </p>
      </header>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{ background: "var(--red-dim)", border: "1px solid var(--red-border)", color: "var(--red)" }}
        >
          {error}
        </div>
      )}

      <AdminTable
        columns={columns}
        rows={rows}
        loading={loading}
        emptyIcon="ticket"
        emptyMessage="No enabled scratch-off sources yet."
        defaultSort={{ key: "activeGameCount", dir: "desc" }}
      />
    </div>
  );
}
